/**
 * ShareMySiloButton — Nov 2026 (Randy).
 *
 * The one thing a fan could never do: share My Silo ITSELF. Every share path
 * ran through a creator's public silo page, so a fan with no silo had no way
 * to hand anyone a seeded link — even though their referral code has existed
 * all along and the home page has honoured `?ref=` for months.
 *
 * Signed in  — press once: copies the link and says "Copied to clipboard" for
 *              3s, then goes back to asking. It used to print the URL on the
 *              button for 10s first: *"an unnecessary step, and also looks
 *              unprofessional… no need to show the link, if it says it's
 *              copied they know they can paste it anywhere."* Correct — the
 *              URL was never the point, the clipboard was.
 * Signed out — opens the fan signup on the share path, which swaps the top of
 *              that card for the referral story.
 *
 * Works for a fan, a creator, or a Pro: every account carries a code.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShareNetwork, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useViewerSurface } from "@/lib/useViewerSurface";

const COPIED_MS = 3000;

export default function ShareMySiloButton({ className = "", label, sublabel, testId = "share-my-silo" }) {
  const { isAnon } = useViewerSurface();
  const nav = useNavigate();
  const [copied, setCopied] = useState(false);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }, []);

  const press = async () => {
    if (isAnon) { nav("/signup?as=fan&via=share"); return; }
    if (copied) return;
    setBusy(true);
    try {
      const link = url || (await api.get("/me/referral-link")).data?.share_url || "";
      if (!link) throw new Error("no link");
      setUrl(link);
      const ok = await copy(link);
      if (!ok) throw new Error("clipboard blocked");
      clearTimeout(timer.current);
      setCopied(true);
      timer.current = setTimeout(() => setCopied(false), COPIED_MS);
    } catch {
      toast.error("Couldn't copy your share link. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={press}
      disabled={busy}
      data-testid={testId}
      data-phase={copied ? "copied" : "idle"}
      title="Share My Silo with your own link"
      className={className}
    >
      {copied ? <Check size={13} weight="bold" className="shrink-0" />
        : <ShareNetwork size={13} weight="duotone" className="shrink-0" />}
      {/* Two lines deep either way, so the pill doesn't resize mid-press. */}
      <span className="min-w-0 min-h-[2.85em] flex flex-col justify-center">
        <span className="block text-center" data-testid={`${testId}-top`}>
          {busy ? "One moment…" : copied ? "Copied to clipboard" : (label || "Know someone you wish had a digital twin?")}
        </span>
        {!copied && !busy && (
          <span className="block font-medium text-center" data-testid={`${testId}-sub`}>{sublabel || "Invite them"}</span>
        )}
      </span>
    </button>
  );
}
